<template>
  <div class="driver-home">
    <!-- 顶部状态栏 -->
    <div class="status-header">
      <div class="driver-info" @click="goToProfile">
        <div class="driver-avatar">{{ driverName ? driverName.charAt(0) : $t('common.unknown').charAt(0) }}</div>
        <div class="driver-details">
          <div class="driver-name">{{ driverName || $t('driverHome.title') }}</div>
          <div class="driver-profile-link">👤 {{ $t('driverHome.myProfile') }}</div>
        </div>
      </div>
      
      <!-- 状态切换 -->
      <el-switch
        v-model="acceptContractorOrders"
        :active-text="$t('driverHome.acceptContractor')"
        :inactive-text="$t('driverHome.rejectContractor')"
        :disabled="switchingStatus"
        @change="toggleAcceptContractorOrders"
        class="duty-switch"
      />
    </div>

    <!-- 快捷操作 -->
    <div class="quick-actions">
      <button class="action-btn" @click="goToGrabHall">
        <div class="action-icon">🎯</div>
        <div class="action-text">{{ $t('driverHome.grabHall') }}</div>
        <el-badge :value="availableContractorCount" :hidden="availableContractorCount === 0" class="action-badge" />
      </button>
      
      <button class="action-btn" @click="goToOrderOnBehalf">
        <div class="action-icon">🛒</div>
        <div class="action-text">{{ $t('driverHome.orderOnBehalf') }}</div>
      </button>
      
      <button class="action-btn" @click="goToOrderList">
        <div class="action-icon">🔍</div>
        <div class="action-text">{{ $t('driverHome.orderQuery') }}</div>
      </button>
      
      <button class="action-btn" @click="showSettings = true">
        <div class="action-icon">⚙️</div>
        <div class="action-text">{{ $t('driverHome.settings') }}</div>
      </button>
    </div>

    <!-- 筛选标签 -->
    <div class="filter-tabs">
      <div :class="['tab', { active: filter === 'all' }]" @click="filter = 'all'">{{ $t('driverHome.filterAll') }} ({{ totalCount }})</div>
      <div :class="['tab', { active: filter === 'pending' }]" @click="filter = 'pending'">{{ $t('driverHome.filterPending') }} ({{ pendingCount }})</div>
      <div :class="['tab', { active: filter === 'delivering' }]" @click="filter = 'delivering'">{{ $t('driverHome.filterDelivering') }} ({{ deliveringCount }})</div>
      <div :class="['tab', { active: filter === 'completed' }]" @click="filter = 'completed'">{{ $t('driverHome.filterCompleted') }} ({{ completedCount }})</div>
      <div :class="['tab', { active: filter === 'after_sales' }]" @click="filter = 'after_sales'">{{ $t('driverHome.filterAfterSales') }}</div>
    </div>

    <!-- 进行中任务 -->
    <div class="active-tasks">
      <div v-if="loadingTasks" class="loading-state">
        <div class="loading-spinner">⏳</div>
        <p>{{ $t('driverHome.loadingTasks') }}</p>
      </div>
      
      <div v-else-if="filteredTasks.length === 0" class="empty-state">
        <div class="empty-icon">😴</div>
        <p>{{ $t('driverHome.noTasks') }}</p>
        <el-button type="primary" size="small" @click="goToGrabHall" v-if="acceptContractorOrders">{{ $t('driverHome.goGrab') }}</el-button>
      </div>
      
      <div v-else>
        <div v-for="task in filteredTasks" :key="task.id" 
             :class="['task-card', selectedTask && selectedTask.id === task.id ? 'selected' : '', taskCategoryClass(task)]"
             @click="selectTask(task)">
          
          <!-- 售后任务标识 -->
          <div v-if="task.afterSalesId" class="task-badge">{{ taskBadgeText(task) }}</div>
          
          <div class="task-header">
            <div class="task-no">{{ task.taskNo }}</div>
            <div class="task-header-right">
              <el-tag :type="getStatusType(task.status)" size="small">{{ getStatusText(task.status) }}</el-tag>
            </div>
          </div>
          
          <div class="task-body">
            <!-- After-sales return pickup: stall pickup → warehouse delivery -->
            <template v-if="task.afterSalesTaskType === 'RETURN_PICKUP'">
              <div class="address-row">
                <div class="address-label">{{ $t('driverHome.returnPickupLabel') }}</div>
                <div class="address-content">
                  <span class="address-text">{{ task.pickupAddress || '-' }}</span>
                </div>
                <el-button size="mini" type="primary" @click.stop="navigateTo(task, 'pickup')">🧭 {{ $t('driverHome.navigate') }}</el-button>
              </div>
              <div class="address-row">
                <div class="address-label">{{ $t('driverHome.returnWarehouseLabel') }}</div>
                <div class="address-content">
                  <span class="address-text">{{ task.deliveryAddress || '-' }}</span>
                </div>
                <el-button size="mini" type="primary" @click.stop="navigateTo(task, 'delivery')">🧭 {{ $t('driverHome.navigate') }}</el-button>
              </div>
            </template>
            <!-- Exchange/resend/normal: warehouse pickup → stall delivery -->
            <template v-else>
              <div class="address-row">
                <div class="address-label">📍 {{ $t('driverHome.pickupPoint') }}</div>
                <div class="address-content">
                  <span class="address-text">{{ task.pickupAddress || $t('driverHome.warehouse') }}</span>
                </div>
                <el-button size="mini" type="primary" @click.stop="navigateTo(task, 'pickup')">🧭 {{ $t('driverHome.navigate') }}</el-button>
              </div>
              <div class="address-row">
                <div class="address-label">🏠 {{ $t('driverHome.deliveryPoint') }}</div>
                <div class="address-content">
                  <span class="address-text">{{ task.deliveryAddress || '-' }}</span>
                </div>
                <el-button size="mini" type="primary" @click.stop="navigateTo(task, 'delivery')">🧭 {{ $t('driverHome.navigate') }}</el-button>
              </div>
            </template>
            <div class="task-row" v-if="task.deliveryContact">
              <span class="label">👤 {{ $t('driverHome.contactPerson') }}:</span>
              <span class="value">{{ task.deliveryContact }} {{ task.deliveryPhone }}</span>
            </div>
            <div class="task-row" v-if="task.fee">
              <span class="label">💰 {{ $t('driverHome.deliveryFee') }}:</span>
              <span class="value amount">S$ {{ task.fee }}</span>
            </div>
          </div>
          
          <!-- After-sales items -->
          <div v-if="task.afterSalesId && task.exchangeItems" class="task-as-items">
            <div class="as-label">📦 {{ task.afterSalesTaskType === 'RETURN_PICKUP' ? $t('driverHome.returnItems') : task.afterSalesTaskType === 'EXCHANGE' ? $t('driverHome.exchangeItems') : $t('driverHome.resendItems') }}:</div>
            <div v-for="(item, idx) in parseTaskItems(task.exchangeItems)" :key="idx" class="as-item">
              {{ item.productName || $t('common.product') }} × {{ item.quantity }}
            </div>
          </div>
          
          <!-- Delivery items -->
          <div v-if="task.orderItems && task.orderItems.length > 0" class="task-as-items">
            <div class="as-label">📦 {{ $t('driverHome.deliveryItems') }} ({{ task.orderItems.length }}):</div>
            <div v-for="(item, idx) in task.orderItems" :key="item.productId || idx" class="as-item">
              <img v-if="item.productImage" :src="item.productImage" class="as-item-img" />
              <span class="as-item-name">{{ item.productName || $t('common.product') }}</span>
              <span class="as-item-qty">× {{ formatQty(item.quantity) }}</span>
            </div>
          </div>
          
          <!-- COD reminder -->
          <div v-if="task.paymentMethod === 'CASH' && !task.codCollected" class="cod-alert">
            💰 {{ $t('driverHome.cashOnDelivery') }}: S$ {{ task.totalAmount }} - {{ $t('driverHome.pleaseCollectCash') }}
          </div>
          
          <div class="task-actions">
            <el-button size="small" type="info" @click.stop="viewOrderDetail(task)" :disabled="!task.orderId">📋 {{ $t('driverHome.orderDetail') }}</el-button>
            <el-button size="small" @click.stop="callCustomer(task)">📞 {{ $t('driverHome.call') }}</el-button>
            <el-button size="small" type="success" @click.stop="printOrder(task)" :disabled="!task.orderId">🖨️ {{ $t('driverHome.printReceipt') }}</el-button>
            <!-- COD collection button -->
            <el-button v-if="task.paymentMethod === 'CASH' && !task.codCollected && (task.status === 'DELIVERING' || task.status === 'COMPLETED')" size="small" type="warning" @click.stop="showCODDialog(task)">
              💰 {{ $t('driverHome.confirmPayment') }}
            </el-button>
            <el-tag v-if="task.codCollected" size="mini" type="success" style="margin-left:8px;">{{ $t('driverHome.collected') }}</el-tag>
            <!-- Exchange/Resend: pending pickup -->
            <el-button v-if="task.status === 'PENDING' && task.afterSalesId && (task.afterSalesTaskType === 'EXCHANGE' || task.afterSalesTaskType === 'RESEND')" size="small" type="warning" @click.stop="showExchangePickupDialog(task)">
              📦 {{ $t('driverHome.goPickNewGoods') }}
            </el-button>
            <!-- Exchange/Resend: delivering -->
            <el-button v-if="task.status === 'DELIVERING' && task.afterSalesId && (task.afterSalesTaskType === 'EXCHANGE' || task.afterSalesTaskType === 'RESEND')" size="small" type="success" @click.stop="showExchangeCompleteDialog(task)">
              🔄 {{ $t('driverHome.completeExchange') }}
            </el-button>
            <!-- Normal/Return: pending -->
            <el-button v-if="task.status === 'PENDING' && (!task.afterSalesId || task.afterSalesTaskType === 'RETURN_PICKUP')" size="small" type="success" @click.stop="pickupTask(task)">
              {{ task.afterSalesId ? '✅ ' + $t('driverHome.confirmPickupReturn') : '✅ ' + $t('driverHome.confirmPickup') }}
            </el-button>
            <!-- Normal/Return: delivering -->
            <el-button v-if="task.status === 'DELIVERING' && (!task.afterSalesId || task.afterSalesTaskType === 'RETURN_PICKUP')" size="small" type="success" @click.stop="completeTask(task)">
              {{ task.afterSalesId && task.afterSalesTaskType === 'RETURN_PICKUP' ? '✅ ' + $t('driverHome.confirmReturned') : $t('driverHome.confirmDelivered') }}
            </el-button>
          </div>
        </div>
      </div>

      <!-- 分页 -->
      <div class="pagination-wrap" v-if="total > pageSize">
        <el-pagination
          background
          layout="total, prev, pager, next"
          :total="total"
          :page-size="pageSize"
          :current-page="currentPage"
          @current-change="handlePageChange"
        />
      </div>
    </div>

    <!-- 打印组件（隐藏） -->
    <bluetooth-print-button
      ref="printBtn"
      button-type="success"
      button-size="mini"
      :disabled="!selectedTask"
      print-type="order"
      :order-data="orderData"
      style="display: none"
    />

    <!-- Exchange pickup dialog -->
    <el-dialog :title="$t('driverHome.pickupNewTitle')" :visible.sync="exchangePickupVisible" width="500px" :close-on-click-modal="false">
      <div v-if="exchangePickupTask">
        <div class="dialog-info"><div>{{ $t('delivery.taskNo') }}: {{ exchangePickupTask.taskNo }}</div><div>{{ $t('delivery.title') }}: {{ exchangePickupTask.afterSalesId ? 'AS-' + exchangePickupTask.afterSalesId : '-' }}</div></div>
        <div class="dialog-section"><div class="section-title">📍 {{ $t('driverHome.pickupWarehouse') }}</div>
          <div class="info-row"><span class="info-label">{{ $t('driverHome.pickupWarehouse') }}</span>{{ exchangePickupTask.pickupContact || '-' }}</div>
          <div class="info-row"><span class="info-label">{{ $t('driverHome.pickupAddress') }}</span>{{ exchangePickupTask.pickupAddress || '-' }}</div>
          <div class="info-row"><span class="info-label">{{ $t('driverHome.pickupPhone') }}</span>{{ exchangePickupTask.pickupPhone || '-' }}</div>
        </div>
        <div class="dialog-section"><div class="section-title">📦 {{ $t('driverHome.pickupList') }}</div>
          <div v-for="(item, idx) in parseTaskItems(exchangePickupTask.exchangeItems)" :key="idx" class="pick-item">
            <span class="pick-name">{{ item.productName || $t('common.product') }}</span><span class="pick-qty">× {{ item.quantity }}</span>
          </div>
        </div>
        <el-input v-model="exchangePickupForm.remark" type="textarea" :rows="2" :placeholder="$t('driverHome.pickupRemark')" />
      </div>
      <div slot="footer">
        <el-button @click="exchangePickupVisible = false">{{ $t('common.cancel') }}</el-button>
        <el-button type="warning" :loading="exchangePickupLoading" @click="submitExchangePickup">✅ {{ $t('driverHome.confirmPickupBtn') }}</el-button>
      </div>
    </el-dialog>

    <!-- Exchange complete dialog -->
    <el-dialog :title="$t('driverHome.completeExchangeTitle')" :visible.sync="exchangeCompleteVisible" width="500px" :close-on-click-modal="false">
      <div v-if="exchangeCompleteTask">
        <div class="dialog-info"><div>{{ $t('delivery.taskNo') }}: {{ exchangeCompleteTask.taskNo }}</div><div>{{ $t('delivery.title') }}: {{ exchangeCompleteTask.afterSalesId ? 'AS-' + exchangeCompleteTask.afterSalesId : '-' }}</div></div>
        <div class="dialog-section"><div class="section-title">📍 {{ $t('driverHome.deliveryStall') }}</div>
          <div class="info-row"><span class="info-label">{{ $t('driverHome.stall') }}</span>{{ exchangeCompleteTask.deliveryContact || '-' }}</div>
          <div class="info-row"><span class="info-label">{{ $t('driverHome.pickupAddress') }}</span>{{ exchangeCompleteTask.deliveryAddress || '-' }}</div>
          <div class="info-row"><span class="info-label">{{ $t('driverHome.pickupPhone') }}</span>{{ exchangeCompleteTask.deliveryPhone || '-' }}</div>
        </div>
        <div class="dialog-section"><div class="section-title">✅ {{ $t('driverHome.pickedGoods') }}</div>
          <div v-for="(item, idx) in parseTaskItems(exchangeCompleteTask.exchangeItems)" :key="idx" class="pick-item">
            <span class="pick-name">{{ item.productName || $t('common.product') }}</span><span class="pick-qty">× {{ item.quantity }}</span>
          </div>
        </div>
        <div class="dialog-section"><div class="section-title">🔄 {{ $t('driverHome.returnOldGoods') }}</div>
          <div v-for="(item, idx) in exchangeCompleteForm.returnItems" :key="idx" class="return-item">
            <span class="return-name">{{ item.productName || $t('common.product') }}</span>
            <el-input-number v-model="item.quantity" :min="0" :max="20" size="mini" style="width:80px;" />
          </div>
        </div>
        <el-input v-model="exchangeCompleteForm.proofReceiverName" :placeholder="$t('driverHome.signReceiver')" style="margin-bottom:8px;" />
        <el-input v-model="exchangeCompleteForm.deliveryNote" type="textarea" :rows="2" :placeholder="$t('driverHome.deliveryRemark')" />
      </div>
      <div slot="footer">
        <el-button @click="exchangeCompleteVisible = false">{{ $t('common.cancel') }}</el-button>
        <el-button type="success" :loading="exchangeCompleteLoading" @click="submitExchangeComplete">✅ {{ $t('driverHome.completeExchangeBtn') }}</el-button>
      </div>
    </el-dialog>

    <!-- COD payment dialog -->
    <el-dialog :title="$t('driverHome.codTitle')" :visible.sync="codDialogVisible" width="400px" :close-on-click-modal="false">
      <div v-if="codDialogTask" class="cod-dialog-content">
        <div class="cod-order-info">
          <div>{{ $t('driverHome.orderNo') }}: {{ codDialogTask.orderNo || '-' }}</div>
          <div>{{ $t('driverHome.customer') }}: {{ codDialogTask.customerName || '-' }}</div>
          <div class="cod-amount">{{ $t('driverHome.receivableAmount') }}: <strong>S$ {{ codDialogTask.totalAmount }}</strong></div>
        </div>
        <el-divider>{{ $t('driverHome.paymentAmount') }}</el-divider>
        <el-input-number v-model="codForm.amount" :precision="2" :min="0" :max="codDialogTask.totalAmount" size="large" style="width:200px;" />
        <el-divider>{{ $t('driverHome.paymentMethod') }}</el-divider>
        <el-radio-group v-model="codForm.paymentMethod" class="cod-radio-group">
          <el-radio-button label="CASH">💵 {{ $t('driverHome.cash') }}</el-radio-button>
          <el-radio-button label="PAYNOW">📱 {{ $t('driverHome.paynow') }}</el-radio-button>
          <el-radio-button label="CREDIT_CARD">💳 {{ $t('driverHome.creditCard') }}</el-radio-button>
        </el-radio-group>
        <el-divider>{{ $t('driverHome.paymentProof') }}</el-divider>
        <div class="cod-upload-area">
          <el-upload
            :action="uploadUrl"
            :headers="uploadHeaders"
            :on-success="handleCODUploadSuccess"
            :on-error="handleCODUploadError"
            :before-upload="beforeUpload"
            :file-list="codFileList"
            list-type="picture-card"
            :limit="3"
            accept="image/*"
          >
            <i class="el-icon-plus"></i>
          </el-upload>
        </div>
        <div class="cod-hint">⚠️ {{ $t('driverHome.codHint') }}</div>
      </div>
      <div slot="footer">
        <el-button @click="codDialogVisible = false">{{ $t('common.cancel') }}</el-button>
        <el-button type="warning" :loading="codDialogLoading" @click="submitCOD">✅ {{ $t('driverHome.confirmPaymentBtn') }}</el-button>
      </div>
    </el-dialog>

    <!-- Settings dialog -->
    <el-dialog
      :title="$t('driverHome.settings')"
      :visible.sync="showSettings"
      width="90%"
      custom-class="driver-settings-dialog"
    >
      <div class="settings-content">
        <div class="setting-item">
          <div class="setting-label">{{ $t('driverHome.acceptContractor') }}</div>
          <el-switch
            v-model="acceptContractorOrders"
            @change="toggleContractorOrders"
          />
        </div>
        
        <div class="setting-item">
          <div class="setting-label">{{ $t('driverHome.vehicleType') }}</div>
          <div class="setting-value">{{ driverInfo.vehicleType || $t('driverHome.notSet') }}</div>
        </div>
        
        <div class="setting-item">
          <div class="setting-label">{{ $t('driverHome.maxLoad') }}</div>
          <div class="setting-value">{{ driverInfo.maxLoad || 0 }}kg</div>
        </div>
        
        <el-divider />
        
        <el-button type="danger" plain block @click="handleLogout">
          ⏻ {{ $t('driverHome.logout') }}
        </el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { getTasksByDriver, getAvailableContractorTasks, updateTaskStatus, collectCOD } from '@/api/delivery'
import api from '@/services/api'
import { confirmPickupFromWholesaler, confirmExchangeAtStall } from '@/services/afterSales'
import BluetoothPrintButton from '@/components/BluetoothPrintButton.vue'

export default {
  name: 'DriverHome',
  components: {
    BluetoothPrintButton
  },
  data() {
    return {
      acceptContractorOrders: false,
      switchingStatus: false,
      driverName: '',
      driverPhone: '',
      driverInfo: {},
      currentDate: '',
      filter: 'all',
      tasks: [],
      selectedTask: null,
      orderData: null,
      loadingTasks: false,
      availableContractorCount: 0,
      showSettings: false,
      currentTask: null,
      total: 0,
      pageSize: 10,
      currentPage: 1,
      // 换货取新货弹窗
      exchangePickupVisible: false,
      exchangePickupTask: null,
      exchangePickupLoading: false,
      exchangePickupForm: { remark: '' },
      // 换货完成弹窗
      exchangeCompleteVisible: false,
      exchangeCompleteTask: null,
      exchangeCompleteLoading: false,
      exchangeCompleteForm: { returnItems: [], proofReceiverName: '', deliveryNote: '' },
      // COD 收款弹窗
      codDialogVisible: false,
      codDialogTask: null,
      codDialogLoading: false,
      codForm: { amount: 0, proofUrl: '', paymentMethod: 'CASH' },
      codFileList: []
    }
  },
  computed: {
    filteredTasks() {
      if (this.filter === 'all') return this.tasks;
      if (this.filter === 'after_sales') return this.tasks.filter(t => t.afterSalesId);
      return this.tasks.filter(t => t.status === this.filter.toUpperCase());
    },
    pendingCount() {
      return this.tasks.filter(t => t.status === 'PENDING').length;
    },
    deliveringCount() {
      return this.tasks.filter(t => t.status === 'DELIVERING').length;
    },
    completedCount() {
      return this.tasks.filter(t => t.status === 'COMPLETED').length;
    },
    totalCount() {
      return this.tasks.length;
    },
    todayRevenue() {
      return this.tasks
        .filter(t => t.status === 'COMPLETED' && t.fee)
        .reduce((sum, t) => sum + parseFloat(t.fee || 0), 0);
    },
    uploadUrl() {
      return (process.env.VUE_APP_API_URL || '/api') + '/files/upload-image';
    },
    uploadHeaders() {
      return { Authorization: 'Bearer ' + localStorage.getItem('auth_token') };
    }
  },
  created() {
    this.initDriverInfo()
    this.updateDate()
    this.loadTasks()
    this.loadAvailableContractorCount()
    
    // 每 30 秒刷新一次数据
    this.refreshInterval = setInterval(() => {
      this.loadTasks()
      this.loadAvailableContractorCount()
    }, 30000)
  },
  beforeDestroy() {
    if (this.refreshInterval) {
      clearInterval(this.refreshInterval)
    }
  },
  methods: {
    initDriverInfo() {
      const driverInfo = JSON.parse(localStorage.getItem('driver_info') || '{}')
      this.driverId = localStorage.getItem('driver_id') ? Number(localStorage.getItem('driver_id')) : null
      this.driverName = driverInfo.name || this.$t('driverHome.title')
      this.driverPhone = driverInfo.phone || ''
      this.driverInfo = driverInfo
      this.acceptContractorOrders = driverInfo.acceptContractorOrders || false
    },
    
    updateDate() {
      const date = new Date()
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      const week = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][date.getDay()]
      this.currentDate = `${year}-${month}-${day} ${week}`
    },
    
    async loadTasks() {
      if (!this.driverId) {
        console.warn('⚠️ driverId empty, skip')
        return
      }
      
      this.loadingTasks = true
      try {
        const res = await getTasksByDriver({
          driverId: this.driverId,
          page: this.currentPage - 1,
          size: 100
        })
        
        this.tasks = res.content || res.data?.content || []
        this.total = res.total || res.data?.total || 0
      } catch (error) {
        console.error('❌ load tasks failed:', error)
        this.$message.error(this.$t('driverHome.operationFailed'))
      } finally {
        this.loadingTasks = false
      }
    },
    
    async loadAvailableContractorCount() {
      try {
        const res = await getAvailableContractorTasks({ page: 0, size: 1 })
        this.availableContractorCount = res.total || res.data?.total || 0
      } catch (error) {
        console.error('load grab tasks failed:', error)
      }
    },
    
    handlePageChange(page) {
      this.currentPage = page
      this.loadTasks()
    },
    
    selectTask(task) {
      this.selectedTask = task
    },
    
    async loadOrderData(orderId) {
      try {
        const res = await api.get(`/orders/${orderId}`)
        this.orderData = res || res.data
      } catch (e) {
        this.$message.error(this.$t('driverHome.operationFailed'))
      }
    },
    
    async printOrder(task) {
      this.selectedTask = task
      if (task.orderId) {
        await this.loadOrderData(task.orderId)
        this.$nextTick(() => {
          const btn = this.$refs.printBtn
          if (btn) btn.handlePrint()
        })
      } else {
        this.$message.warning(this.$t('driverHome.noLinkedOrder'))
      }
    },
    
    callCustomer(task) {
      const phone = task.deliveryPhone || task.pickupPhone
      if (phone) window.location.href = `tel:${phone}`
    },
    
    navigateTo(task, type) {
      const isPickup = type === 'pickup'
      const lat = isPickup ? task.pickupGpsLat : task.deliveryGpsLat
      const lng = isPickup ? task.pickupGpsLng : task.deliveryGpsLng
      const addr = isPickup ? (task.pickupAddress || 'Warehouse') : (task.deliveryAddress || '-')
      
      if (lat && lng) {
        window.open(`https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}&destination_place_id=${encodeURIComponent(addr)}`, '_blank')
      } else if (addr) {
        window.open(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(addr)}`, '_blank')
      }
    },
    
    taskCategoryClass(task) {
      if (!task.afterSalesId) return ''
      if (task.afterSalesTaskType === 'RETURN_PICKUP') return 'as-pickup'
      return 'as-resend'
    },
    
    taskBadgeText(task) {
      if (!task.afterSalesId) return ''
      const map = {
        RETURN_PICKUP: '🔧 ' + this.$t('driverHome.returnItems'),
        EXCHANGE: '🔄 ' + this.$t('driverHome.exchange'),
        RESEND: '📦 ' + this.$t('driverHome.resend'),
        EXCHANGE_DELIVERY: '🔄 ' + this.$t('driverHome.exchange'),
        RESEND_DELIVERY: '📦 ' + this.$t('driverHome.resend')
      }
      return map[task.afterSalesTaskType] || this.$t('driverHome.afterSalesBadge')
    },
    
    parseTaskItems(json) {
      try { return json ? JSON.parse(json) : []; } catch { return []; }
    },
    formatQty(qty) {
      if (qty == null) return '0'
      const num = parseFloat(qty)
      return Number.isInteger(num) ? num.toString() : num.toFixed(2)
    },
    showExchangePickupDialog(task) {
      this.exchangePickupTask = task;
      this.exchangePickupForm = { remark: '' };
      this.exchangePickupVisible = true;
    },
    showCODDialog(task) {
      this.codDialogTask = task;
      this.codForm.amount = task.totalAmount || 0;
      this.codForm.proofUrl = '';
      this.codForm.paymentMethod = 'CASH';
      this.codFileList = [];
      this.codDialogVisible = true;
    },
    async submitCOD() {
      if (!this.codDialogTask) return;
      if (!this.codForm.proofUrl) {
        this.$message.warning(this.$t('driverHome.uploadProofFirst'));
        return;
      }
      try {
        this.codDialogLoading = true;
        await collectCOD(this.codDialogTask.id, {
          driverId: this.driverId,
          amount: this.codForm.amount,
          proofUrl: this.codForm.proofUrl,
          paymentMethod: this.codForm.paymentMethod
        });
        this.$message.success(this.$t('driverHome.paymentSuccess'));
        this.codDialogVisible = false;
        this.loadTasks();
      } catch (e) {
        this.$message.error(this.$t('driverHome.paymentFailed') + ': ' + (e.message || e));
      } finally {
        this.codDialogLoading = false;
      }
    },
    handleCODUploadSuccess(response) {
      if (response.url) {
        this.codForm.proofUrl = response.url;
        this.$message.success(this.$t('driverHome.uploadProofSuccess'));
      }
    },
    handleCODUploadError() {
      this.$message.error(this.$t('driverHome.uploadProofFailed'));
    },
    beforeUpload(file) {
      const isImage = file.type.startsWith('image/');
      if (!isImage) {
        this.$message.error(this.$t('driverHome.onlyImage'));
      }
      const isLt5M = file.size / 1024 / 1024 < 5;
      if (!isLt5M) {
        this.$message.error(this.$t('driverHome.imageMax5MB'));
      }
      return isImage && isLt5M;
    },
    async submitExchangePickup() {
      this.exchangePickupLoading = true;
      try {
        await confirmPickupFromWholesaler(this.exchangePickupTask.id);
        this.$message.success(this.$t('driverHome.pickupSuccess'));
        this.exchangePickupVisible = false;
        this.loadTasks();
      } catch (e) {
        this.$message.error(this.$t('driverHome.operationFailed') + ': ' + (e.response?.data?.message || e.message));
      } finally { this.exchangePickupLoading = false; }
    },
    showExchangeCompleteDialog(task) {
      this.exchangeCompleteTask = task;
      const items = this.parseTaskItems(task.exchangeItems);
      this.exchangeCompleteForm = {
        returnItems: items.map(item => ({ productId: item.productId, productName: item.productName, quantity: item.quantity })),
        proofReceiverName: '',
        deliveryNote: ''
      };
      this.exchangeCompleteVisible = true;
    },
    async submitExchangeComplete() {
      this.exchangeCompleteLoading = true;
      try {
        const data = {
          returnItems: JSON.stringify(this.exchangeCompleteForm.returnItems),
          proofReceiverName: this.exchangeCompleteForm.proofReceiverName,
          deliveryNote: this.exchangeCompleteForm.deliveryNote
        };
        await confirmExchangeAtStall(this.exchangeCompleteTask.id, data);
        this.$message.success(this.$t('driverHome.exchangeComplete'));
        this.exchangeCompleteVisible = false;
        this.loadTasks();
      } catch (e) {
        this.$message.error(this.$t('driverHome.operationFailed') + ': ' + (e.response?.data?.message || e.message));
      } finally { this.exchangeCompleteLoading = false; }
    },
    async completeTask(task) {
      try {
        await updateTaskStatus(task.id, 'COMPLETED')
        
        if (task.afterSalesId && task.afterSalesTaskType === 'RETURN_PICKUP') {
          await api.post(`/after-sales/${task.afterSalesId}/receive`, null)
          this.$message.success(this.$t('driverHome.afterSalesDelivered'))
        } else if (task.afterSalesId && (task.afterSalesTaskType === 'EXCHANGE' || task.afterSalesTaskType === 'RESEND')) {
          // 旧任务兼容：使用旧接口
          await api.post(`/after-sales/${task.afterSalesId}/ship`)
          this.$message.success(this.$t('driverHome.afterSalesShipped'))
        } else {
          this.$message.success(this.$t('driverHome.delivered'))
        }
        this.loadTasks()
      } catch (error) {
        this.$message.error(this.$t('driverHome.operationFailed'))
      }
    },
    
    async pickupTask(task) {
      try {
        const driverId = localStorage.getItem('driver_id')
        await api.post(`/delivery/tasks/${task.id}/pickup`, null, {
          params: { driverId: Number(driverId) }
        })
        
        if (task.afterSalesId && (task.afterSalesTaskType === 'EXCHANGE' || task.afterSalesTaskType === 'RESEND')) {
          // 使用新接口
          await confirmPickupFromWholesaler(task.id)
          this.$message.success(this.$t('driverHome.pickupSuccess'))
        } else {
          this.$message.success(this.$t('driverHome.pickupDelivering'))
        }
        this.loadTasks()
      } catch (error) {
        this.$message.error(this.$t('driverHome.operationFailed'))
      }
    },
    
    async toggleContractorOrders(value) {
      try {
        this.$message.success(value ? this.$t('driverHome.contractorOn') : this.$t('driverHome.contractorOff'))
      } catch (error) {
        this.$message.error(this.$t('driverHome.operationFailed'))
        this.acceptContractorOrders = !value
      }
    },
    
    async toggleAcceptContractorOrders(value) {
      this.switchingStatus = true
      try {
        const driverId = this.driverId
        if (!driverId) {
          this.$message.warning(this.$t('driverHome.loginFirst'))
          return
        }
        
        await api.patch(`/delivery/drivers/${driverId}/accept-contractor-orders`, null, {
          params: { accept: value }
        })
        
        const driverInfo = JSON.parse(localStorage.getItem('driver_info') || '{}')
        driverInfo.acceptContractorOrders = value
        localStorage.setItem('driver_info', JSON.stringify(driverInfo))
        
        this.$message.success(value ? this.$t('driverHome.contractorOn') : this.$t('driverHome.contractorOff'))
      } catch (error) {
        console.error('toggle contractor failed:', error)
        this.$message.error(this.$t('driverHome.operationFailed'))
        this.acceptContractorOrders = !value
      } finally {
        this.switchingStatus = false
      }
    },
    
    goToGrabHall() {
      this.$router.push('/delivery/grab-hall')
    },
    
    goToOrderOnBehalf() {
      this.$router.push('/delivery/order-on-behalf')
    },
    
    goToOrderList() {
      this.$router.push('/delivery/orders')
    },
    goToProfile() {
      this.$router.push('/delivery/profile')
    },
    viewOrderDetail(task) {
      if (task.orderId) {
        this.$router.push(`/delivery/orders/${task.orderId}`)
      }
    },
    
    handleLogout() {
      this.$confirm(this.$t('driverHome.logoutConfirm'), this.$t('driverHome.tip'), {
        confirmButtonText: this.$t('common.confirm'),
        cancelButtonText: this.$t('common.cancel'),
        type: 'warning'
      }).then(() => {
        localStorage.removeItem('driver_id')
        localStorage.removeItem('driver_info')
        localStorage.removeItem('driver_token')
        this.$router.push('/delivery/login')
      }).catch(() => {})
    },
    
    getStatusType(status) {
      const map = { PENDING: 'warning', DELIVERING: 'success', COMPLETED: 'info' }
      return map[status] || 'info'
    },
    
    getStatusText(status) {
      const map = { PENDING: this.$t('driverHome.pendingPickup'), DELIVERING: this.$t('driverHome.delivering'), COMPLETED: this.$t('driverHome.completed') }
      return map[status] || status
    }
  }
}
</script>

<style lang="scss" scoped>
.driver-home {
  min-height: 100vh;
  background: #f5f7fa;
}

.status-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  
  .driver-info {
    display: flex;
    align-items: center;
    gap: 12px;
    
    .driver-avatar {
      width: 48px;
      height: 48px;
      border-radius: 50%;
      background: rgba(255, 255, 255, 0.3);
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 20px;
      font-weight: bold;
    }
    
    .driver-details {
      .driver-name {
        font-size: 16px;
        font-weight: 600;
        margin-bottom: 4px;
      }
      
      .driver-phone {
        font-size: 12px;
        opacity: 0.9;
      }
    }
  }
}

.stats-section {
  padding: 20px;
  margin-top: -10px;
  
  .stats-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 16px;
    
    h2 {
      font-size: 18px;
      font-weight: 600;
      color: #2c3e50;
      margin: 0;
    }
    
    .stats-date {
      font-size: 12px;
      color: #999;
    }
  }
  
  .stats-grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 12px;
    
    .stat-card {
      background: white;
      border-radius: 12px;
      padding: 16px 12px;
      text-align: center;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
      
      .stat-icon {
        font-size: 24px;
        margin-bottom: 8px;
      }
      
      .stat-value {
        font-size: 20px;
        font-weight: bold;
        margin-bottom: 4px;
        
        &.primary { color: #667eea; }
        &.warning { color: #ff9800; }
        &.success { color: #4caf50; }
        &.info { color: #2196f3; }
      }
      
      .stat-label {
        font-size: 11px;
        color: #999;
      }
    }
  }
}

.quick-actions {
  background: white;
  display: flex;
  width: 100%;
  
  .action-btn {
      flex: 1;
      background: none;
      border: none;
      padding: 14px 0;
      text-align: center;
      cursor: pointer;
      transition: background 0.2s;
      position: relative;
      border-right: 1px solid #f0f0f0;
      
      &:last-child {
        border-right: none;
      }
      
      &:active {
        background: #f5f5f5;
      }
      
      .action-icon {
        font-size: 24px;
        display: block;
        margin-bottom: 6px;
      }
      
      .action-text {
        font-size: 12px;
        color: #666;
        display: block;
      }
      
      .action-badge {
        position: absolute;
        top: 6px;
        right: 50%;
        margin-right: -24px;
      }
    }
  }

.filter-tabs {
  display: flex;
  background: white;
  padding: 12px 20px;
  gap: 8px;
  overflow-x: auto;
  
  .tab {
    padding: 8px 16px;
    background: #f5f7fa;
    border-radius: 20px;
    cursor: pointer;
    white-space: nowrap;
    font-size: 13px;
    &.active { background: #667eea; color: white; }
  }
}

.active-tasks {
  padding: 0 20px;
  
  .task-card {
    background: white;
    border-radius: 12px;
    padding: 16px;
    margin-bottom: 12px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
    cursor: pointer;
    transition: all 0.2s;
    position: relative;
    
    &.selected { border: 2px solid #667eea; }
    
    // 售后取件 - 橙色左边框
    &.as-pickup {
      border-left: 4px solid #fa8c16;
      background: #fff7e6;
    }
    // 换货/补发 - 紫色左边框
    &.as-resend {
      border-left: 4px solid #722ed1;
      background: #f9f0ff;
    }
    
    .task-badge {
      position: absolute;
      top: 8px;
      right: 12px;
      font-size: 11px;
      padding: 2px 8px;
      border-radius: 10px;
      background: #fff3cd;
      color: #856404;
    }
    
    .task-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 12px;
      
      .task-no {
        font-size: 14px;
        font-weight: 600;
        color: #667eea;
      }
    }
    
    .task-body {
      margin-bottom: 16px;
      
      .address-row {
        display: flex;
        align-items: center;
        padding: 8px 0;
        border-bottom: 1px dashed #f0f0f0;
        gap: 8px;
        
        &:last-child { border-bottom: none; }
        
        .address-label {
          font-size: 12px;
          font-weight: 600;
          color: #667eea;
          min-width: 60px;
          flex-shrink: 0;
        }
        
        .address-content {
          flex: 1;
          min-width: 0;
        }
        
        .address-text {
          font-size: 13px;
          color: #333;
          word-break: break-all;
        }
      }
      
      .task-row {
        display: flex;
        margin-top: 8px;
        font-size: 13px;
        
        .label {
          color: #999;
          min-width: 70px;
          flex-shrink: 0;
        }
        
        .value {
          color: #333;
          flex: 1;
          
          &.amount {
            color: #ff6b35;
            font-weight: 600;
          }
        }
      }
    }
    
    .task-as-items {
      padding: 8px 12px;
      background: #f8f9fa;
      border-radius: 8px;
      margin-bottom: 12px;
      font-size: 12px;

      .as-label { color: #666; margin-bottom: 6px; font-weight: 600; }
      .as-no { color: #999; }

      .as-item {
        display: flex;
        align-items: center;
        gap: 8px;
        padding: 4px 0;
        border-bottom: 1px solid #eee;

        &:last-child {
          border-bottom: none;
        }

        .as-item-img {
          width: 32px;
          height: 32px;
          border-radius: 4px;
          object-fit: cover;
          flex-shrink: 0;
        }

        .as-item-name {
          flex: 1;
          color: #333;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .as-item-qty {
          color: #999;
          flex-shrink: 0;
        }
      }
    }
    
    .task-actions {
      display: flex;
      gap: 8px;
      flex-wrap: wrap;
    }
    
    .cod-alert {
      margin: 8px 12px;
      padding: 10px 14px;
      background: #FFF8E1;
      border-left: 4px solid #FF9800;
      border-radius: 4px;
      font-size: 14px;
      color: #E65100;
      font-weight: 600;
    }
    
    .cod-dialog-content {
      .cod-order-info {
        padding: 12px;
        background: #f5f5f5;
        border-radius: 8px;
        margin-bottom: 12px;
        .cod-amount {
          margin-top: 8px;
          font-size: 16px;
          strong { color: #E65100; }
        }
      }
      .cod-hint {
        margin-top: 12px;
        font-size: 12px;
        color: #999;
      }
      .cod-radio-group {
        width: 100%;
        display: flex;
        .el-radio-button {
          flex: 1;
          .el-radio-button__inner {
            width: 100%;
          }
        }
      }
      .cod-upload-area {
        .el-upload--picture-card {
          width: 80px;
          height: 80px;
          line-height: 80px;
        }
        .el-upload-list__item {
          width: 80px;
          height: 80px;
        }
      }
    }
  }
  
  .loading-state, .empty-state {
    text-align: center;
    padding: 40px 20px;
    color: #999;
    
    .loading-spinner, .empty-icon {
      font-size: 48px;
      margin-bottom: 12px;
    }
    
    p { margin-bottom: 16px; }
  }
  
  .pagination-wrap {
    padding: 20px 0;
    text-align: center;
  }
}

.settings-content {
  .setting-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 12px 0;
    
    .setting-label { font-size: 14px; color: #333; }
    .setting-value { font-size: 14px; color: #666; }
  }
}

// 移动端优化
@media (max-width: 768px) {
  .driver-home { padding: 0; }
  
  .status-header {
    padding: 12px 15px;
    
    .driver-info {
      gap: 10px;
      
      .driver-avatar {
        width: 40px;
        height: 40px;
        font-size: 18px;
      }
      
      .driver-details {
        .driver-name { font-size: 14px; }
        .driver-phone { font-size: 11px; }
      }
    }
    
    .duty-switch ::v-deep .el-switch__label {
      font-size: 12px !important;
    }
  }
  
  .stats-section {
    padding: 15px;
    margin-top: 0;
    
    .stats-header {
      margin-bottom: 12px;
      h2 { font-size: 16px !important; }
      .stats-date { font-size: 11px !important; }
    }
    
    .stats-grid {
      gap: 10px;
      
      .stat-card {
        padding: 12px 8px;
        
        .stat-icon { font-size: 20px; margin-bottom: 6px; }
        .stat-value { font-size: 18px !important; }
        .stat-label { font-size: 11px; }
      }
    }
  }
  
  .quick-actions {
    .action-btn {
      padding: 12px 0;
      .action-icon { font-size: 22px; margin-bottom: 4px; }
      .action-text { font-size: 11px; }
    }
  }
  
  .filter-tabs {
    padding: 10px 15px;
  }
  
  .active-tasks {
    padding: 0 15px;
    
    .task-card {
      padding: 12px;
      margin-bottom: 10px;
      
      .task-header { margin-bottom: 10px; .task-no { font-size: 13px; } }
      
      .task-body {
        margin-bottom: 12px;
        
        .address-row { flex-wrap: wrap; gap: 6px; padding: 8px 0;
          .address-label { min-width: 50px; font-size: 11px; }
          .address-text { font-size: 12px; }
        }
        .task-row { font-size: 12px; .label { min-width: 60px; font-size: 11px; } }
      }
      
      .task-actions {
        gap: 6px;
        .el-button { padding: 8px 12px !important; font-size: 12px !important; }
      }
    }
    
    .loading-state, .empty-state { padding: 40px 15px; }
  }
  
  ::v-deep .el-dialog {
    width: 90% !important;
    max-width: 320px !important;
    margin: 20px auto !important;
    .el-dialog__body { padding: 15px !important; }
  }
}
.dialog-info { display: flex; gap: 16px; font-size: 12px; color: #999; margin-bottom: 12px; }
.dialog-section { margin-bottom: 12px; }
.section-title { font-size: 13px; font-weight: 600; color: #333; margin-bottom: 6px; }
.info-row { display: flex; font-size: 12px; padding: 4px 0; }
.info-row .info-label { color: #999; width: 50px; flex-shrink: 0; }
.pick-item { display: flex; justify-content: space-between; padding: 6px 8px; background: #f5f7fa; border-radius: 4px; margin: 4px 0; font-size: 13px; }
.pick-item .pick-name { color: #333; }
.pick-item .pick-qty { color: #667eea; font-weight: 500; }
.return-item { display: flex; justify-content: space-between; align-items: center; padding: 6px 8px; background: #fff7e6; border-radius: 4px; margin: 4px 0; font-size: 13px; }
.return-item .return-name { color: #333; }
.as-item { font-size: 12px; color: #666; padding: 2px 0; }
</style>
